import { usePrevious } from '@volvo-cars/react-utils';
import { useCallback, useEffect, useRef, useState } from 'react';

export type ScrollDirection = 'up' | 'down' | null;
/**
 * Returns the last direction the user scrolled. Possible values are
 *
 *  - `up`
 *  - `down`
 *  - `null` if the user did not scroll far enough yet.
 */
export function useScrollDirection({
  disabled,
  offsetThreshold = 0,
  onChange,
  scrollElement,
}: {
  disabled?: boolean;
  offsetThreshold?: number;
  onChange?(direction: ScrollDirection): void;
  scrollElement?: HTMLElement;
}) {
  const [direction, setDirection] = useState<ScrollDirection>(null);
  const prevPageHeight = useRef(0);
  const prevScrollElement = usePrevious(scrollElement);
  const prevScrollY = useRef(0);
  const scrollTicking = useRef(false);

  const updateScrollDirection = useCallback(() => {
    scrollTicking.current = false;
    const pageHeight =
      document.querySelector('body')?.getBoundingClientRect().height || 0;
    const scrollY =
      window.pageYOffset + (scrollElement ? scrollElement.scrollTop : 0);

    // Ignore scrolls from 0, triggered by released scroll lock
    if (prevScrollY.current <= 0 || scrollY === prevScrollY.current) {
      prevScrollY.current = scrollY;
      return;
    }

    // Keeps hook from infinitely triggering if hook is used to change the height of an element
    if (prevPageHeight.current === pageHeight) {
      setDirection(
        scrollY < offsetThreshold
          ? null
          : prevScrollY.current < scrollY
            ? 'down'
            : 'up',
      );
    }

    prevScrollY.current = scrollY;
    prevPageHeight.current = pageHeight;
  }, [offsetThreshold, scrollElement]);

  const handleScroll = useCallback(() => {
    if (!scrollTicking.current) {
      scrollTicking.current = true;
      window.requestAnimationFrame(updateScrollDirection);
    }
  }, [updateScrollDirection]);

  useEffect(() => {
    if (disabled) return;

    prevPageHeight.current =
      document.querySelector('body')?.getBoundingClientRect().height || 0;

    window.addEventListener('scroll', handleScroll);

    if (scrollElement !== prevScrollElement) {
      prevScrollElement &&
        //@ts-ignore
        prevScrollElement.removeEventListener('scroll', handleScroll);

      scrollElement && scrollElement.addEventListener('scroll', handleScroll);
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, [disabled, prevScrollElement, scrollElement, handleScroll]);

  // Trigger onChange handler when direction changes
  useEffect(() => {
    if (onChange) onChange(direction);
  }, [onChange, direction]);

  // Reset scroll direction if hook gets disabled
  useEffect(() => {
    if (disabled) {
      setDirection(null);
    }
  }, [disabled]);

  return { scrollDirection: direction };
}
